import React, { useEffect, useState, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Badge, Card, Col, Row, Table } from 'react-bootstrap';
import { FaLock } from 'react-icons/fa';
import { BsArrowLeft } from 'react-icons/bs';
import PageHeader from 'Utils/PageHeader';
import FetchContainer from 'Utils/FetchContainer';
import { engineLabels, EngineIcon, getDisplayDate, getDisplayTime } from 'Utils';
import { isFileBasedDatasourceType } from 'Utils/fileTypeUtils';
import AppContext from 'Utils/AppContext';
import FlaggedFields from 'SensitiveDataLandscape/DataSourceRiskProfile/FlaggedFields';
import MaskingStatus from '../MaskingSummary/MaskingStatus';
import { getEncryptionSummary } from './api';

/** Encrypt = green (success); Decrypt = orange (warning) — matches the landscape job-results table. */
function statusBadgeVariant(status, operation) {
  if (status === 'FAILED') return 'danger';
  if (status === 'PENDING' || status === 'RUNNING') return 'info';
  if (status === 'CANCELLED') return 'warning';
  if (status === 'SUCCEEDED') return operation === 'Decryption' ? 'warning' : 'success';
  return 'secondary';
}

function statusLabel(status, operation) {
  if (status === 'SUCCEEDED') return operation === 'Decryption' ? 'DECRYPTED' : 'ENCRYPTED';
  if (status === 'RUNNING' || status === 'PENDING') return operation === 'Decryption' ? 'DECRYPTING' : 'ENCRYPTING';
  if (status === 'FAILED') return 'ERROR';
  return status || '—';
}

/** Color-coded encrypt/decrypt operation history for this source, newest first. */
const OperationHistory = ({ rows }) => {
  if (!rows || rows.length === 0) {
    return <p className='text-muted mb-0'>No encryption or decryption jobs have run for this data source yet.</p>;
  }
  return (
    <Table size='sm' responsive className='mb-0'>
      <thead>
        <tr>
          <th>Operation</th>
          <th>When</th>
          <th>Status</th>
          <th>Results</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const isDecrypt = r.operation === 'Decryption';
          return (
            <tr key={`${r.operation}-${r.jobId}`}>
              <td>{r.operation}</td>
              <td>{r.date ? `${getDisplayDate(r.date)} @ ${getDisplayTime(r.date)}` : '—'}</td>
              <td>
                <Badge variant={statusBadgeVariant(r.status, r.operation)} className='text-uppercase'>
                  {statusLabel(r.status, r.operation)}
                </Badge>
                {r.errorMessage ? <div className='small text-danger mt-1'>{r.errorMessage}</div> : null}
              </td>
              <td>
                {r.jobId != null ? (
                  <Link to={`/secure/${isDecrypt ? 'decrypt' : 'encrypt'}/results/${r.jobId}`}>View</Link>
                ) : (
                  '—'
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};

const EncryptionSummary = () => {
  const { id } = useParams();
  const { connections } = useContext(AppContext);
  const [summary, setSummary] = useState(null);
  const [fetchingData, setFetchingData] = useState(true);
  const [error, setError] = useState(null);

  const source = (connections || []).find((c) => c && String(c.id) === String(id));
  const sourceName = source?.name || `Data source #${id}`;
  const engine = source?.type || '';
  const isFileBased = isFileBasedDatasourceType(source?.datasourceType);

  useEffect(() => {
    const init = async () => {
      setFetchingData(true);
      setError(null);
      try {
        const result = await getEncryptionSummary(id);
        setSummary(result);
      } catch (e) {
        console.log(e);
        setError(e.message);
      }
      setFetchingData(false);
    };
    if (id) init();
  }, [id]);

  const data = summary || {};

  return (
    <FetchContainer fetchingData={fetchingData} absolute={true} error={error}>
      <div>
        <Link to='/secure' className='d-inline-flex align-items-center mb-2' style={{ fontSize: '14px' }}>
          <BsArrowLeft className='mr-1' /> Secure Data Landscape
        </Link>
        <div className='title-div'>
          <PageHeader
            header={sourceName}
            tooltip='Review what was encrypted for this data source and the encrypt / decrypt history over time.'
            icon={FaLock}
          />
          {engine && (
            <p>
              {/* Engine note doubles as a shortcut to this source's editor. */}
              <Link
                to={`/sources/source-editor/${encodeURIComponent(id)}`}
                title='Open the data source editor'
                data-testid='summary-engine-source-link'
              >
                <EngineIcon engine={engine} /> {engineLabels[engine] || engine}
              </Link>
            </p>
          )}
        </div>
        <div>
          <div><h5 className='text-left h-secondary'>ENCRYPTION SUMMARY</h5></div>
          <Card>
            <Card.Body>
              <Row className='mb-1'>
                <Col xs='4' className='pl-1 pr-1'>
                  <MaskingStatus
                    heading='ENCRYPTION STATS'
                    rowsLabel='Rows processed'
                    rowsMasked={data.rowsProcessed}
                    durationMs={data.durationMs}
                    errorCount={data.errorCount}
                  />
                </Col>
                <Col className='flex-grow-1 pl-1 pr-1'>
                  <FlaggedFields
                    totalTables={data.totalTables}
                    totalColumns={data.totalFields}
                    sensitiveTables={data.maskedTables}
                    sensitiveColumns={data.maskedFields}
                    isFileBased={isFileBased}
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>
          <Row className='mb-1'>
            <Col>
              <div><h5 className='text-left h-secondary'>ENCRYPT / DECRYPT HISTORY</h5></div>
              <Card>
                <Card.Body>
                  <OperationHistory rows={data.historyRows} />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </FetchContainer>
  );
};

export default EncryptionSummary;
